/**
 * LinkedIn Direct Publishing Service
 * 
 * Uses "Direct Access Token" mode — the same approach used by Buffer, Hootsuite,
 * and Zapier. No server-side OAuth callback required.
 *
 * How to get your LinkedIn Access Token:
 * 1. Go to https://www.linkedin.com/developers/apps → your app → Auth tab
 * 2. Under "OAuth 2.0 tools" click "Request access token"
 * 3. Select scopes: openid, profile, email, w_member_social
 * 4. Copy the generated token (valid for 60 days)
 *
 * Alternatively, use the LinkedIn Token Generator:
 * https://www.linkedin.com/developers/tools/oauth/token-generator
 */

const STORAGE_KEY_TOKEN   = 'uxcf_linkedin_access_token';
const STORAGE_KEY_PROFILE = 'uxcf_linkedin_profile';

export interface LinkedInProfile {
  accessToken: string;
  personUrn: string;   // urn:li:person:XXXX
  name: string;
  email?: string;
  picture?: string;
  connectedAt: string;
  tokenNote?: string;
}

export interface LinkedInPostResult {
  success: boolean;
  post_urn?: string;
  post_url?: string;
  author_name?: string;
  message?: string;
  error?: string;
}

/* ─── Token Storage ─────────────────────────────────────────────── */

export function saveLinkedInProfile(profile: LinkedInProfile): void {
  localStorage.setItem(STORAGE_KEY_PROFILE, JSON.stringify(profile));
  localStorage.setItem(STORAGE_KEY_TOKEN, profile.accessToken);
}

export function getLinkedInProfile(): LinkedInProfile | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PROFILE);
    return raw ? (JSON.parse(raw) as LinkedInProfile) : null;
  } catch {
    return null;
  }
}

export function clearLinkedInProfile(): void {
  localStorage.removeItem(STORAGE_KEY_PROFILE);
  localStorage.removeItem(STORAGE_KEY_TOKEN);
}

export function getStoredToken(): string {
  return localStorage.getItem(STORAGE_KEY_TOKEN) || '';
}

/* ─── Verify Token & Fetch Profile ─────────────────────────────── */

export interface VerifyResult {
  ok: boolean;
  personUrn?: string;
  name?: string;
  email?: string;
  picture?: string;
  error?: string;
}

export async function verifyLinkedInToken(accessToken: string): Promise<VerifyResult> {
  if (!accessToken.trim()) return { ok: false, error: 'Access token is empty.' };

  try {
    // Use the LinkedIn userinfo endpoint (OpenID Connect)
    const res = await fetch('https://api.linkedin.com/v2/userinfo', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'LinkedIn-Version': '202406',
      },
    });

    if (res.status === 401) return { ok: false, error: 'Invalid or expired token. Please generate a new one.' };
    if (res.status === 403) return { ok: false, error: 'Token missing required scopes (openid, profile, email).' };
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      return { ok: false, error: `LinkedIn API error (${res.status}): ${body.slice(0, 120)}` };
    }

    const data = await res.json() as Record<string, string>;
    const personUrn = data.sub ? `urn:li:person:${data.sub}` : '';
    return {
      ok: true,
      personUrn,
      name: data.name || data.given_name || 'LinkedIn User',
      email: data.email,
      picture: data.picture,
    };
  } catch (err) {
    // CORS error — LinkedIn blocks direct browser calls to userinfo on some setups
    // Fall back to a minimal profile constructed from the token itself
    const isCorsError =
      err instanceof TypeError &&
      (String(err).toLowerCase().includes('fetch') || String(err).toLowerCase().includes('cors'));

    if (isCorsError) {
      // Accept the token anyway; URN will be entered manually or via the composer
      return {
        ok: true,
        personUrn: '',
        name: 'LinkedIn User (token accepted)',
        error: 'CORS_WARN: Could not auto-fetch profile — please paste your Author URN below.',
      };
    }
    return { ok: false, error: String(err) };
  }
}

/* ─── Publish Post ──────────────────────────────────────────────── */

export async function publishToLinkedIn(params: {
  accessToken: string;
  personUrn: string;      // urn:li:person:XXXXX
  title: string;
  caption?: string;
  excerpt: string;
  body?: string;
  hashtags?: string[];
  imageUrl?: string;
  articleUrl?: string;
  postType?: 'feed_post' | 'article_link_card';
}): Promise<LinkedInPostResult> {
  const { accessToken, personUrn } = params;

  if (!accessToken) return { success: false, error: 'No access token configured. Connect LinkedIn first.' };
  if (!personUrn)   return { success: false, error: 'Author URN missing. Enter it in the connection panel (e.g. urn:li:person:XXXX).' };

  /* ── Build post text ─────────────────────────────────────────── */
  const hashtagStr =
    params.hashtags && params.hashtags.length > 0
      ? '\n\n' + params.hashtags.map((h) => (h.startsWith('#') ? h : `#${h}`)).join(' ')
      : '';

  const captionBlock = params.caption ? `${params.caption}\n\n` : '';
  const excerptBlock = params.excerpt || '';
  const postText = `${params.title}\n\n${captionBlock}${excerptBlock}${hashtagStr}`.trim().slice(0, 3000);

  /* ── UGC Post payload ────────────────────────────────────────── */
  type ShareContent = {
    shareCommentary: { text: string };
    shareMediaCategory: string;
    media?: Array<{
      status: string;
      description?: { text: string };
      originalUrl: string;
      title?: { text: string };
    }>;
  };

  const shareContent: ShareContent =
    params.postType === 'article_link_card' && params.articleUrl
      ? {
          shareCommentary:   { text: postText },
          shareMediaCategory: 'ARTICLE',
          media: [
            {
              status:      'READY',
              description: { text: params.excerpt.slice(0, 256) },
              originalUrl: params.articleUrl,
              title:       { text: params.title.slice(0, 200) },
            },
          ],
        }
      : {
          shareCommentary:   { text: postText },
          shareMediaCategory: 'NONE',
        };

  const ugcPayload = {
    author:         personUrn,
    lifecycleState: 'PUBLISHED',
    specificContent: { 'com.linkedin.ugc.ShareContent': shareContent },
    visibility:      { 'com.linkedin.ugc.MemberNetworkVisibility': 'PUBLIC' },
  };

  try {
    const res = await fetch('https://api.linkedin.com/v2/ugcPosts', {
      method: 'POST',
      headers: {
        Authorization:    `Bearer ${accessToken}`,
        'Content-Type':   'application/json',
        'X-Restli-Protocol-Version': '2.0.0',
        'LinkedIn-Version': '202406',
      },
      body: JSON.stringify(ugcPayload),
    });

    const raw = await res.text();
    let data: Record<string, unknown> = {};
    try { data = JSON.parse(raw); } catch { /* keep empty */ }

    if (!res.ok) {
      // LinkedIn CORS block when called directly from a browser
      const isCors = res.status === 0 || res.type === 'opaque';
      if (isCors) {
        return {
          success: false,
          error:
            'LinkedIn blocks direct API calls from browsers (CORS). ' +
            'Your post content has been prepared — please use the LinkedIn app or paste via the "Copy Post" button below.',
        };
      }
      const errMsg =
        (data?.message as string) ||
        (data?.error as string) ||
        raw.slice(0, 200);
      return { success: false, error: `LinkedIn API ${res.status}: ${errMsg}` };
    }

    const postUrn = data?.id as string | undefined;
    return {
      success:     true,
      post_urn:    postUrn,
      post_url:    postUrn
        ? `https://www.linkedin.com/feed/update/${postUrn}/`
        : 'https://www.linkedin.com/feed/',
      author_name: params.title,
      message:     'Post published successfully!',
    };
  } catch (err) {
    const msg = String(err);
    if (msg.toLowerCase().includes('cors') || msg.toLowerCase().includes('fetch')) {
      return {
        success: false,
        error:
          'Network blocked (CORS policy). LinkedIn restricts direct API calls from web apps. ' +
          'Use the "Copy Post" button to copy formatted content and paste it on LinkedIn directly.',
      };
    }
    return { success: false, error: msg };
  }
}

/* ─── Legacy stubs (kept for backward compat) ───────────────────── */
export function getSessionId(): string {
  let sid = localStorage.getItem('uxcf_linkedin_session');
  if (!sid) { sid = crypto.randomUUID(); localStorage.setItem('uxcf_linkedin_session', sid); }
  return sid;
}
export async function startLinkedInOAuth(): Promise<{ authUrl: string; sessionId: string; redirectUri: string } | { error: string }> {
  return { error: 'OAuth mode is not available without a backend server. Use Direct Token mode instead.' };
}
export async function checkLinkedInConnection(): Promise<{ connected: boolean }> {
  return { connected: !!getLinkedInProfile() };
}
export async function getLinkedInPostHistory(): Promise<unknown[]> {
  return [];
}
